import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import './OrderDetail.css';

const localeMap = { lt: 'lt-LT', en: 'en-US', de: 'de-DE' };

function OrderDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t, language } = useLanguage();
  const locale = localeMap[language] || 'lt-LT';
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});
  const [returnReason, setReturnReason] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [paying, setPaying] = useState(false);

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', id, language],
    queryFn: async () => {
      const res = await apiClient.get(`/orders/${id}`);
      return res.data;
    },
  });

  // Gauti esamus grąžinimus šiam užsakymui
  const { data: existingReturns } = useQuery({
    queryKey: ['order-returns', id],
    queryFn: async () => {
      try {
        const res = await apiClient.get(`/user/returns?order_id=${id}`);
        return res.data.returns || [];
      } catch (e) {
        return [];
      }
    },
    enabled: !!order && order.payment_status === 'paid',
  });

  const createReturnMutation = useMutation({
    mutationFn: async (returnData) => {
      const res = await apiClient.post('/user/returns', returnData);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['order-returns', id]);
      queryClient.invalidateQueries(['order', id]);
      setShowReturnForm(false);
      setSelectedItems({});
      setReturnReason('');
      setReturnNotes('');
      notification.success(t('returnSuccess'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('returnError'));
    },
  });

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: t('pending'), color: '#f59e0b', bg: '#fef3c7' },
      processing: { label: t('orderStatusProcessing'), color: '#3b82f6', bg: '#dbeafe' },
      shipped: { label: t('orderStatusShipped'), color: '#6366f1', bg: '#e0e7ff' },
      delivered: { label: t('orderStatusDelivered'), color: '#10b981', bg: '#d1fae5' },
      cancelled: { label: t('orderStatusCancelled'), color: '#ef4444', bg: '#fee2e2' },
    };
    return statusMap[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  const getPaymentStatusInfo = (status) => {
    const statusMap = {
      pending: { label: t('pending'), color: '#f59e0b', bg: '#fef3c7' },
      paid: { label: t('paid'), color: '#10b981', bg: '#d1fae5' },
      failed: { label: t('failed'), color: '#ef4444', bg: '#fee2e2' },
      refunded: { label: t('refunded'), color: '#6366f1', bg: '#e0e7ff' },
    };
    return statusMap[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  const getReturnStatusInfo = (status) => {
    const statusMap = {
      requested: { label: t('returnRequested'), color: '#f59e0b', bg: '#fef3c7' },
      approved: { label: t('returnApproved'), color: '#3b82f6', bg: '#dbeafe' },
      rejected: { label: t('returnRejected'), color: '#ef4444', bg: '#fee2e2' },
      received: { label: t('returnReceived'), color: '#6366f1', bg: '#e0e7ff' },
      refunded: { label: t('refunded'), color: '#10b981', bg: '#d1fae5' },
      cancelled: { label: t('orderStatusCancelled'), color: '#6b7280', bg: '#f3f4f6' },
    };
    return statusMap[status] || { label: status, color: '#6b7280', bg: '#f3f4f6' };
  };

  const canPayOrder = order && order.payment_status !== 'paid' && ['stripe', 'paypal', 'banklink'].includes((order.payment_method || '').toLowerCase());

  const handlePayNow = async () => {
    if (!order || paying || !canPayOrder) return;
    setPaying(true);
    try {
      const res = await apiClient.post('/payment/create', {
        order_id: order.id,
        payment_method: order.payment_method,
      });
      const data = res.data || {};
      const session = data.session || {};
      const method = (data.payment_method || order.payment_method || '').toLowerCase();
      if (method === 'stripe' && session.url) {
        window.location.href = session.url;
        return;
      }
      if (method === 'stripe' && session.client_secret) {
        window.location.href = `${window.location.origin}/payment/stripe?order_id=${order.id}&client_secret=${session.client_secret}`;
        return;
      }
      if (method === 'paypal' && session.approval_url) {
        window.location.href = session.approval_url;
        return;
      }
      if (method === 'banklink' && session.redirect_url) {
        window.location.href = session.redirect_url;
        return;
      }
      notification.error(t('paymentSessionError'));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('paymentSessionError');
      notification.error(msg);
      setPaying(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="order-detail-page">
        <div className="order-detail-container">
          <div className="loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-page">
        <div className="order-detail-container">
          <div className="error-state">
            <h2>{t('orderNotFound')}</h2>
            <p>{t('orderNotFoundText')}</p>
            <Link to="/orders" className="btn btn-primary">
              {t('backToOrders')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(order.status);
  const paymentStatusInfo = getPaymentStatusInfo(order.payment_status);

  return (
    <div className="order-detail-page">
      <div className="order-detail-container">
        <Link to="/orders" className="back-link">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"></path>
          </svg>
          {t('backToOrders')}
        </Link>

        <div className="order-detail-header">
          <div className="order-header-main">
            <div className="order-number-section">
              <span className="order-label">{t('orderLabel')}</span>
              <h1 className="order-number">#{order.order_number}</h1>
            </div>
            <p className="order-date">{formatDateTime(order.created_at)}</p>
          </div>
          <div className="order-status-badges">
            <div className="order-badge-item">
              <span className="order-badge-label">{t('orderStatusLabel')}</span>
              <span className="status-badge" style={{ 
                backgroundColor: statusInfo.bg,
                color: statusInfo.color 
              }}>
                {statusInfo.label}
              </span>
            </div>
            <div className="order-badge-item">
              <span className="order-badge-label">{t('paymentStatusLabel')}</span>
              <span className="payment-badge" style={{ 
                backgroundColor: paymentStatusInfo.bg,
                color: paymentStatusInfo.color 
              }}>
                {paymentStatusInfo.label}
              </span>
              {canPayOrder && (
                <button
                  type="button"
                  onClick={handlePayNow}
                  disabled={paying}
                  className="payment-pay-now-badge"
                  style={{
                    marginLeft: '8px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#10b981',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: paying ? 'wait' : 'pointer',
                  }}
                >
                  {paying ? t('redirectingToPayment') : t('payNow')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="order-detail-grid">
          <div className="order-info-card">
            <h2 className="card-title">{t('orderInfo')}</h2>
            <div className="info-list">
              <div className="info-item">
                <span className="info-label">{t('orderNumber')}</span>
                <span className="info-value">#{order.order_number}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('orderDate')}</span>
                <span className="info-value">{formatDate(order.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('time')}</span>
                <span className="info-value">{new Date(order.created_at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('paymentMethod')}</span>
                <span className="info-value">
                  {order.payment_method === 'bank_transfer' ? t('bankTransfer') : (order.payment_method || t('notSpecified'))}
                </span>
              </div>
            </div>
          </div>

          {order.billing_address && (
            <div className="order-info-card">
              <h2 className="card-title">{t('billingAddress')}</h2>
              <div className="address-block">
                <p className="address-name">
                  {order.billing_address.first_name} {order.billing_address.last_name}
                </p>
                {order.billing_address.company && (
                  <p className="address-company">{order.billing_address.company}</p>
                )}
                <p className="address-line">{order.billing_address.address_line1}</p>
                {order.billing_address.address_line2 && (
                  <p className="address-line">{order.billing_address.address_line2}</p>
                )}
                <p className="address-city">
                  {order.billing_address.postal_code} {order.billing_address.city}
                </p>
                <p className="address-country">{order.billing_address.country}</p>
                {order.billing_address.phone && (
                  <p className="address-phone">{order.billing_address.phone}</p>
                )}
              </div>
            </div>
          )}

          {order.shipping_address && (
            <div className="order-info-card">
              <h2 className="card-title">{t('shippingAddress')}</h2>
              <div className="address-block">
                <p className="address-name">
                  {order.shipping_address.first_name} {order.shipping_address.last_name}
                </p>
                {order.shipping_address.company && (
                  <p className="address-company">{order.shipping_address.company}</p>
                )}
                <p className="address-line">{order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && (
                  <p className="address-line">{order.shipping_address.address_line2}</p>
                )}
                <p className="address-city">
                  {order.shipping_address.postal_code} {order.shipping_address.city}
                </p>
                <p className="address-country">{order.shipping_address.country}</p>
                {order.shipping_address.phone && (
                  <p className="address-phone">{order.shipping_address.phone}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="order-items-card">
          <h2 className="card-title">{t('orderItems')}</h2>
          <div className="order-items-list">
            {order.items && order.items.map((item) => (
              <div key={item.id} className="order-item-card">
                <div className="order-item-image">
                  {item.image ? (
                    <img src={item.image} alt={item.product_name} />
                  ) : (
                    <div className="item-image-placeholder">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                        <path d="M9 9h6v6H9z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                <div className="order-item-info">
                  <h3 className="item-name">{item.product_name}</h3>
                  {item.product_sku && (
                    <p className="item-sku">SKU: {item.product_sku}</p>
                  )}
                  <p className="item-quantity">{t('quantity')}: {item.quantity}</p>
                </div>
                <div className="order-item-pricing">
                  <p className="item-unit-price">
                    {item.price ? parseFloat(item.price).toFixed(2) : '0.00'} €
                  </p>
                  <p className="item-total-price">
                    {item.total ? parseFloat(item.total).toFixed(2) : '0.00'} €
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="order-summary-card">
          <h2 className="card-title">{t('orderSummary')}</h2>
          <div className="summary-list">
            <div className="summary-item">
              <span className="summary-label">{t('subtotal')}</span>
              <span className="summary-value">
                {order.subtotal ? parseFloat(order.subtotal).toFixed(2) : '0.00'} €
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('taxLabel21')}</span>
              <span className="summary-value">
                {order.tax ? parseFloat(order.tax).toFixed(2) : '0.00'} €
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('shipping')}</span>
              <span className="summary-value">
                {order.shipping_cost ? parseFloat(order.shipping_cost).toFixed(2) : '0.00'} €
              </span>
            </div>
            <div className="summary-item summary-total">
              <span className="summary-label">{t('total')}</span>
              <span className="summary-value">
                {order.total ? parseFloat(order.total).toFixed(2) : '0.00'} €
              </span>
            </div>
          </div>
        </div>

        {order.notes && (
          <div className="order-notes-card">
            <h3 className="notes-title">{t('orderNotesLabel')}</h3>
            <p className="notes-text">{order.notes}</p>
          </div>
        )}

        {order.payment_instructions && (
          <div className="order-info-card order-bank-instructions">
            <h2 className="card-title">{t('bankTransferInstructions')}</h2>
            <div className="bank-instructions-list">
              {(order.payment_instructions.bank_accounts && order.payment_instructions.bank_accounts.length > 0
                ? order.payment_instructions.bank_accounts
                : (order.payment_instructions.iban
                    ? [{ iban: order.payment_instructions.iban, bank_name: order.payment_instructions.bank_name || '', company_name: order.payment_instructions.company_name || '' }]
                    : [])
              ).map((account, idx) => (
                <div key={idx} className="bank-account-block">
                  {order.payment_instructions.bank_accounts?.length > 1 && (
                    <h3 className="bank-account-block-title">{t('bankAccountLabel')} {idx + 1}</h3>
                  )}
                  {(account.iban || account.bank_name || account.company_name) && (
                    <>
                      {account.iban && (
                        <div className="bank-instruction-item">
                          <span className="bank-label">{t('iban')}</span>
                          <span className="bank-value bank-iban">{account.iban}</span>
                        </div>
                      )}
                      {account.bank_name && (
                        <div className="bank-instruction-item">
                          <span className="bank-label">{t('bankName')}</span>
                          <span className="bank-value">{account.bank_name}</span>
                        </div>
                      )}
                      {account.company_name && (
                        <div className="bank-instruction-item">
                          <span className="bank-label">{t('companyName')}</span>
                          <span className="bank-value">{account.company_name}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              <div className="bank-instruction-item">
                <span className="bank-label">{t('paymentReference')}</span>
                <span className="bank-value bank-reference">{order.payment_instructions.reference}</span>
              </div>
              <div className="bank-instruction-item bank-amount">
                <span className="bank-label">{t('amountToPay')}</span>
                <span className="bank-value">
                  {order.payment_instructions.amount != null
                    ? `${Number(order.payment_instructions.amount).toFixed(2)} ${order.payment_instructions.currency || 'EUR'}`
                    : ''}
                </span>
              </div>
              {order.payment_instructions.instructions && (
                <div className="bank-instruction-item bank-instructions-text">
                  <p className="bank-value">{order.payment_instructions.instructions}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {order.invoice && (
          <div className="order-invoice-card">
            <h2 className="card-title">{t('invoiceTitle')}</h2>
            <div className="invoice-info-list">
              <div className="invoice-info-item">
                <span className="invoice-label">{t('invoiceNumber')}</span>
                <span className="invoice-value">{order.invoice.invoice_number}</span>
              </div>
              {order.invoice.issue_date && (
                <div className="invoice-info-item">
                  <span className="invoice-label">{t('issueDate')}</span>
                  <span className="invoice-value">
                    {new Date(order.invoice.issue_date).toLocaleDateString('lt-LT')}
                  </span>
                </div>
              )}
              {order.invoice.due_date && (
                <div className="invoice-info-item">
                  <span className="invoice-label">{t('dueDate')}</span>
                  <span className="invoice-value">
                    {new Date(order.invoice.due_date).toLocaleDateString('lt-LT')}
                  </span>
                </div>
              )}
              <div className="invoice-info-item">
                <span className="invoice-label">{t('orderStatus')}</span>
                <span className="invoice-value">
                  {order.invoice.status === 'sent' ? t('orderStatusShipped') : 
                   order.invoice.status === 'paid' ? t('paid') : 
                   order.invoice.status === 'draft' ? t('draft') : 
                   order.invoice.status}
                </span>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  if (!token) {
                    notification.warning(t('loginRequiredInvoice'));
                    return;
                  }

                  const response = await apiClient.get(`/orders/${order.id}/invoice/pdf`, {
                    responseType: 'blob',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'X-Preferred-Language': language || 'lt',
                    },
                  });
                  
                  if (response.data.type === 'application/json') {
                    const text = await response.data.text();
                    const errorData = JSON.parse(text);
                    throw new Error(errorData.error || t('errorDownloadingPdf'));
                  }
                  
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `saskaita-${order.invoice.invoice_number}.pdf`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch (error) {
                  const errorMessage = error.response?.data 
                    ? (typeof error.response.data === 'string' 
                        ? error.response.data 
                        : error.response.data.error || 'Nepavyko parsisiųsti PDF')
                    : error.message || t('errorDownloadingPdf');
                  notification.error(errorMessage);
                }
              }}
              className="invoice-download-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {t('downloadPdfInvoice')}
            </button>
          </div>
        )}

        {order.payment_status === 'paid' && (
          <div className="order-returns-card">
            <h2 className="card-title">{t('productReturns')}</h2>
            
            {existingReturns && existingReturns.length > 0 && (
              <div className="existing-returns-section">
                <h3 className="section-subtitle">{t('yourReturnRequests')}</h3>
                <div className="returns-list">
                  {existingReturns.map((returnItem) => {
                    const returnStatusInfo = getReturnStatusInfo(returnItem.status);
                    return (
                      <div key={returnItem.id} className="return-card">
                        <div className="return-card-header">
                          <div className="return-number-section">
                            <span className="return-label">{t('returnNumberLabel')}</span>
                            <span className="return-number">{returnItem.return_number}</span>
                          </div>
                          <div className="return-status-badge" style={{ 
                            backgroundColor: returnStatusInfo.bg,
                            color: returnStatusInfo.color 
                          }}>
                            {returnStatusInfo.label}
                          </div>
                        </div>
                        <div className="return-card-body">
                          <div className="return-info-grid">
                            <div className="return-info-item">
                              <span className="return-info-label">{t('reasonLabel')}</span>
                              <span className="return-info-value">{returnItem.reason}</span>
                            </div>
                            <div className="return-info-item">
                              <span className="return-info-label">{t('refundAmount')}</span>
                              <span className="return-info-value">
                                {parseFloat(returnItem.refund_amount || 0).toFixed(2)} €
                              </span>
                            </div>
                            <div className="return-info-item">
<span className="return-info-label">{t('orderDate')}</span>
                            <span className="return-info-value">
                              {new Date(returnItem.created_at).toLocaleDateString(locale)}
                            </span>
                          </div>
                            {returnItem.tracking_number && (
                              <div className="return-info-item">
                                <span className="return-info-label">{t('trackingNumber')}</span>
                                <span className="return-info-value">{returnItem.tracking_number}</span>
                              </div>
                            )}
                          </div>
                          {returnItem.notes && (
                            <div className="return-notes">
                              <span className="return-notes-label">{t('orderNotesLabel')}</span>
                              <p className="return-notes-text">{returnItem.notes}</p>
                            </div>
                          )}
                        </div>
                        {returnItem.status === 'requested' && (
                          <div className="return-card-footer">
                            <button
                              onClick={async () => {
                                const confirmed = await notification.showConfirm(
                                  t('cancelReturnConfirm'),
                                  t('confirmCancelReturn')
                                );
                                if (confirmed) {
                                  try {
                                    await apiClient.put(`/user/returns/${returnItem.id}/cancel`);
                                    queryClient.invalidateQueries(['order-returns', id]);
                                    notification.success(t('returnCancelled'));
                                  } catch (error) {
                                    notification.error(error.response?.data?.error || t('errorCancellingReturn'));
                                  }
                                }
                              }}
                              className="btn btn-secondary btn-sm"
                            >
                              {t('cancelReturn')}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(!existingReturns || existingReturns.length === 0 || existingReturns.every(r => r.status === 'cancelled' || r.status === 'rejected')) && (
              <>
                {!showReturnForm ? (
                  <button
                    onClick={() => setShowReturnForm(true)}
                    className="return-init-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 12h18M12 3v18"></path>
                    </svg>
                    {t('initiateReturn')}
                  </button>
                ) : (
                  <div className="return-form-card">
                    <h3 className="form-title">{t('newReturnRequest')}</h3>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const items = Object.keys(selectedItems)
                          .filter(itemId => selectedItems[itemId] > 0)
                          .map(itemId => ({
                            order_item_id: parseInt(itemId),
                            quantity: parseInt(selectedItems[itemId])
                          }));

                        if (items.length === 0) {
                          notification.warning(t('selectAtLeastOneItem'));
                          return;
                        }

                        if (!returnReason.trim()) {
                          notification.warning(t('returnReasonRequired'));
                          return;
                        }

                        createReturnMutation.mutate({
                          order_id: parseInt(id),
                          reason: returnReason,
                          items,
                          notes: returnNotes || null
                        });
                      }}
                    >
                      <div className="form-group">
                        <label>{t('selectItemsForReturn')}</label>
                        <div className="return-items-selection">
                          {order.items && order.items.map((item) => {
                            const maxQuantity = item.quantity;
                            const selectedQuantity = parseInt(selectedItems[item.id] || 0);
                            
                            return (
                              <div key={item.id} className="return-item-selection">
                                <label className="return-item-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={selectedQuantity > 0}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedItems(prev => ({
                                          ...prev,
                                          [item.id]: maxQuantity
                                        }));
                                      } else {
                                        setSelectedItems(prev => {
                                          const newItems = { ...prev };
                                          delete newItems[item.id];
                                          return newItems;
                                        });
                                      }
                                    }}
                                  />
                                  <span>{item.product_name}</span>
                                </label>
                                {selectedQuantity > 0 && (
                                  <div className="quantity-selector">
                                    <label>
                                      {t('quantity')}:
                                      <input
                                        type="number"
                                        min="1"
                                        max={maxQuantity}
                                        value={selectedQuantity}
                                        onChange={(e) => {
                                          const qty = Math.max(1, Math.min(maxQuantity, parseInt(e.target.value) || 1));
                                          setSelectedItems(prev => ({
                                            ...prev,
                                            [item.id]: qty
                                          }));
                                        }}
                                        className="input"
                                      />
                                      <span className="quantity-max">/ {maxQuantity}</span>
                                    </label>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>{t('returnReasonLabel')}</label>
                        <select
                          value={returnReason}
                          onChange={(e) => setReturnReason(e.target.value)}
                          required
                          className="input"
                        >
                          <option value="">{t('selectReasonPlaceholder')}</option>
                          <option value="defective">{t('reasonDefective')}</option>
                          <option value="wrong_item">{t('reasonWrongItem')}</option>
                          <option value="not_as_described">{t('reasonNotAsDescribed')}</option>
                          <option value="damaged">{t('reasonDamaged')}</option>
                          <option value="changed_mind">{t('reasonChangedMind')}</option>
                          <option value="other">{t('reasonOther')}</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>{t('additionalNotes')}</label>
                        <textarea
                          value={returnNotes}
                          onChange={(e) => setReturnNotes(e.target.value)}
                          className="input"
                          rows="4"
                          placeholder={t('returnNotesPlaceholder')}
                        />
                      </div>

                      <div className="form-actions">
                        <button
                          type="button"
                          onClick={() => {
                            setShowReturnForm(false);
                            setSelectedItems({});
                            setReturnReason('');
                            setReturnNotes('');
                          }}
                          className="btn btn-secondary"
                        >
                          {t('cancel')}
                        </button>
                        <button
                          type="submit"
                          disabled={createReturnMutation.isLoading}
                          className="btn btn-primary"
                        >
                          {createReturnMutation.isLoading ? t('creatingOrder') : t('submitReturnRequest')}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderDetail;
